import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import { connect } from "react-redux";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { FormattedMessage, injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { register, getRegisterConfig, authErrorMessage } from "../_redux/authCrud";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import PasswordInput from "../../../../components/PasswordInput";
import TurnstileWidget from "../../../../components/TurnstileWidget";

// Batch 1 redesign - ONE unified account creation form, collecting only
// identity fields (name/email/password). There is no Individual/Company
// choice here anymore - that decision (and everything organisation-related)
// is made AFTER email verification + login, on the Welcome/Onboarding
// screen (see frontend/src/app/modules/Onboarding). "Create identity
// first, collect organisational information later."
//
// There is still no "join an existing company" option here by design -
// anyone wanting to join an existing organisation must ask that
// organisation's admin to add them instead (see ManageUser/AddUser.js).
const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  acceptTerms: false
};

function Registration(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);

  const [isError, setisError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setisSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  // Server-provided: the public Turnstile key and a signed form token (proof the form was open for a moment).
  const [config, setConfig] = useState(null);
  const [configFailed, setConfigFailed] = useState(false);
  const [token, setToken] = useState("");
  const [widgetKey, setWidgetKey] = useState(0);
  const [widgetError, setWidgetError] = useState("");
  // A hidden field that people never see or fill; scripts often do.
  const [homepage, setHomepage] = useState("");

  const loadConfig = useCallback(() => {
    setConfigFailed(false);
    getRegisterConfig()
      .then(({ data }) => setConfig(data))
      .catch(() => setConfigFailed(true));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const challengeRequired = !!(config && config.turnstile && config.turnstile.enabled);
  const challengeUnavailable = !!(config && config.turnstile && !config.turnstile.available);
  const readyToSubmit = !!config && !challengeUnavailable && (!challengeRequired || !!token);

  const RegistrationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "Minimum 2 symbols")
      .max(50, "Maximum 50 symbols")
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    lastName: Yup.string()
      .min(2, "Minimum 2 symbols")
      .max(50, "Maximum 50 symbols")
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    email: Yup.string()
      .email("Wrong email format")
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    password: Yup.string()
      .min(6, "Minimum 6 symbols")
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required(intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })),
    acceptTerms: Yup.bool().required("You must accept the terms and conditions")
  });

  const getInputClasses = fieldname => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }
    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }
    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: RegistrationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      setLoading(true);
      setisError(false);
      register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        turnstileToken: token,
        formToken: config && config.formToken,
        homepage
      })
        .then(() => {
          setRegisteredEmail(values.email);
          setisSuccess(true);
          setLoading(false);
          setSubmitting(false);
        })
        .catch(err => {
          setSubmitting(false);
          setLoading(false);
          setErrorMessage(authErrorMessage(err, "Error occurred while trying to register. Please try again."));
          setisError(true);
          // A challenge response is single use: always ask for a new one, and a new form token if this one expired.
          setToken("");
          setWidgetKey(k => k + 1);
          const code = err && err.response && err.response.data && err.response.data.code;
          if (code === "FORM_REJECTED") loadConfig();
        });
    }
  });

  return (
    <div className="login-form login-signin" style={{ display: "block" }}>
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          <FormattedMessage id="AUTH.REGISTER.TITLE" />
        </h3>
        <p className="text-muted font-weight-bold">
          Create your Anike account - it only takes a moment
        </p>
      </div>

      {isSuccess ? (
        <div
          className="alert alert-custom alert-light-success fade show mb-10"
          role="alert"
        >
          <div className="alert-icon">
            <span className="svg-icon svg-icon-3x svg-icon-success">
              <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
            </span>
          </div>
          <div className="alert-text font-weight-bold">
            If this email address can be registered, we've sent a verification link to{" "}
            <strong>{registeredEmail}</strong>. Please check your inbox and spam folder
            and click the link to verify your email address before logging in.
            <div className="mt-4">
              <Link to="/auth/resend-verification" className="font-weight-bold">
                Didn't get the email?
              </Link>
              {" "}or{" "}
              <Link to="/auth/login" className="font-weight-bold">
                sign in
              </Link>
              {" "}if you already have an account.
            </div>
          </div>
        </div>
      ) : (
        <form
          id="kt_login_signin_form"
          className="form fv-plugins-bootstrap fv-plugins-framework"
          onSubmit={formik.handleSubmit}
        >
          {isError && (
            <div
              className="alert alert-custom alert-light-danger fade show mb-10"
              role="alert"
            >
              <div className="alert-icon">
                <span className="svg-icon svg-icon-3x svg-icon-danger">
                  <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                </span>
              </div>
              <div className="alert-text font-weight-bold">
                {errorMessage}
              </div>
              <div className="alert-close" onClick={() => setisError(false)}>
                <button type="button" className="close" data-dismiss="alert" aria-label="Close">
                  <span aria-hidden="true"><i className="ki ki-close"></i></span>
                </button>
              </div>
            </div>
          )}

          <div className="form-group fv-plugins-icon-container">
            <input
              placeholder="First name"
              type="text"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("firstName")}`}
              name="firstName"
              {...formik.getFieldProps("firstName")}
            />
            {formik.touched.firstName && formik.errors.firstName ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.firstName}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <input
              placeholder="Last name"
              type="text"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("lastName")}`}
              name="lastName"
              {...formik.getFieldProps("lastName")}
            />
            {formik.touched.lastName && formik.errors.lastName ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.lastName}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <input
              placeholder="Email"
              type="email"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("email")}`}
              name="email"
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.email}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <PasswordInput
              placeholder="Password"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("password")}`}
              name="password"
              {...formik.getFieldProps("password")}
            />
            {formik.touched.password && formik.errors.password ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.password}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group fv-plugins-icon-container">
            <PasswordInput
              placeholder="Confirm password"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses("confirmPassword")}`}
              name="confirmPassword"
              {...formik.getFieldProps("confirmPassword")}
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.confirmPassword}</div>
              </div>
            ) : null}
          </div>

          <div className="form-group mt-4">
            <label className="checkbox">
              <input
                type="checkbox"
                name="acceptTerms"
                className="m-1"
                {...formik.getFieldProps("acceptTerms")}
              />
              <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="mr-1">I agree the Terms &amp; Conditions</Link>
              <span />
            </label>
            {formik.touched.acceptTerms && formik.errors.acceptTerms ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.acceptTerms}</div>
              </div>
            ) : null}
          </div>

          {/* Honeypot: off-screen and skipped by keyboard and screen readers; people never fill it. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Leave this field empty
              <input type="text" name="homepage" tabIndex={-1} autoComplete="off" value={homepage} onChange={e => { const value = e.target.value; setHomepage(value); }} />
            </label>
          </div>

          {configFailed && (
            <div className="alert alert-light-danger" role="alert">
              We could not prepare the sign-up form.{" "}
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={loadConfig}>
                Try again
              </button>
            </div>
          )}
          {challengeUnavailable && (
            <div className="alert alert-light-danger" role="alert">
              Sign-up is temporarily unavailable. Please try again later.
            </div>
          )}
          {challengeRequired && !challengeUnavailable && (
            <div className="form-group">
              <TurnstileWidget
                siteKey={config.turnstile.siteKey}
                action={config.turnstile.action}
                resetKey={widgetKey}
                onToken={value => {
                  setWidgetError("");
                  setToken(value);
                }}
                onExpire={() => setToken("")}
                onError={message => {
                  setToken("");
                  setWidgetError(message);
                }}
              />
              <div aria-live="polite" className="text-center text-muted font-size-sm mt-2">
                {widgetError || (token ? "Security check complete." : "Please complete the security check above.")}
              </div>
            </div>
          )}

          <div className="form-group d-flex flex-wrap flex-center">
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms || !readyToSubmit}
              className="btn btn-primary font-weight-bold px-9 py-4 my-3 mx-4"
            >
              <span>Create account</span>
              {loading && <span className="ml-3 spinner spinner-white"></span>}
            </button>

            <Link to="/auth/login">
              <button type="button" className="btn btn-light-primary font-weight-bold px-9 py-4 my-3 mx-4">
                Cancel
              </button>
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Registration));

